import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, SpeakerHigh, Headphones, WaveTriangle } from "@phosphor-icons/react";
import { Slider } from "./ui/slider";

function parseFrequency(text) {
  if (!text) return 432;
  const match = text.match(/(\d+)\s*Hz/i);
  return match ? parseInt(match[1], 10) : 432;
}

const BINAURAL_PRESETS = [
  { id: "theta", name: "Deep Calm", beatHz: 6, band: "Theta 4-8 Hz", desc: "Deep meditation & relaxation" },
  { id: "alpha", name: "Gentle Focus", beatHz: 10, band: "Alpha 8-14 Hz", desc: "Calm awareness & light meditation" },
  { id: "delta", name: "Deep Rest", beatHz: 3, band: "Delta 1-4 Hz", desc: "Deep sleep & healing" },
  { id: "low-beta", name: "Mindful Energy", beatHz: 16, band: "Beta 14-30 Hz", desc: "Alert focus & concentration" },
];

export default function FrequencyPlayer({
  frequencyText,
  accentColor = "#2D6A4F",
  compact = false,
  autoPlay = false,
  onPlayStateChange,
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState([35]);
  const [mode, setMode] = useState("pure"); // "pure" | "binaural"
  const [binauralPreset, setBinauralPreset] = useState(BINAURAL_PRESETS[0]);
  const [showPresets, setShowPresets] = useState(false);
  const audioCtxRef = useRef(null);
  const nodesRef = useRef(null);
  const hz = parseFrequency(frequencyText);

  const stopAudio = useCallback(() => {
    const nodes = nodesRef.current;
    const ctx = audioCtxRef.current;
    if (nodes && ctx) {
      const now = ctx.currentTime;
      nodes.masterGain.gain.cancelScheduledValues(now);
      nodes.masterGain.gain.setValueAtTime(nodes.masterGain.gain.value, now);
      nodes.masterGain.gain.linearRampToValueAtTime(0, now + 0.4);
      setTimeout(() => {
        nodes.oscillators.forEach((osc) => { try { osc.stop(); } catch {} });
        nodesRef.current = null;
      }, 500);
    }
    setIsPlaying(false);
    onPlayStateChange?.(false);
  }, [onPlayStateChange]);

  const startPureTone = useCallback((ctx, masterGain) => {
    const oscillators = [];

    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = "sine";
    osc1.frequency.setValueAtTime(hz, ctx.currentTime);
    gain1.gain.setValueAtTime(0.5, ctx.currentTime);
    osc1.connect(gain1);
    gain1.connect(masterGain);
    osc1.start();
    oscillators.push(osc1);

    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = "sine";
    osc2.frequency.setValueAtTime(hz * 2, ctx.currentTime);
    gain2.gain.setValueAtTime(0.08, ctx.currentTime);
    osc2.connect(gain2);
    gain2.connect(masterGain);
    osc2.start();
    oscillators.push(osc2);

    if (hz > 200) {
      const osc3 = ctx.createOscillator();
      const gain3 = ctx.createGain();
      osc3.type = "sine";
      osc3.frequency.setValueAtTime(hz / 2, ctx.currentTime);
      gain3.gain.setValueAtTime(0.12, ctx.currentTime);
      osc3.connect(gain3);
      gain3.connect(masterGain);
      osc3.start();
      oscillators.push(osc3);
    }

    return oscillators;
  }, [hz]);

  const startBinauralBeat = useCallback((ctx, masterGain) => {
    const oscillators = [];
    const beatOffset = binauralPreset.beatHz;

    // Left ear: base frequency
    const oscLeft = ctx.createOscillator();
    const gainLeft = ctx.createGain();
    const panLeft = ctx.createStereoPanner();
    oscLeft.type = "sine";
    oscLeft.frequency.setValueAtTime(hz, ctx.currentTime);
    gainLeft.gain.setValueAtTime(0.45, ctx.currentTime);
    panLeft.pan.setValueAtTime(-1, ctx.currentTime);
    oscLeft.connect(gainLeft);
    gainLeft.connect(panLeft);
    panLeft.connect(masterGain);
    oscLeft.start();
    oscillators.push(oscLeft);

    // Right ear: base frequency + beat offset
    const oscRight = ctx.createOscillator();
    const gainRight = ctx.createGain();
    const panRight = ctx.createStereoPanner();
    oscRight.type = "sine";
    oscRight.frequency.setValueAtTime(hz + beatOffset, ctx.currentTime);
    gainRight.gain.setValueAtTime(0.45, ctx.currentTime);
    panRight.pan.setValueAtTime(1, ctx.currentTime);
    oscRight.connect(gainRight);
    gainRight.connect(panRight);
    panRight.connect(masterGain);
    oscRight.start();
    oscillators.push(oscRight);

    // Subtle center pad for body (very quiet, centered)
    const oscPad = ctx.createOscillator();
    const gainPad = ctx.createGain();
    oscPad.type = "sine";
    oscPad.frequency.setValueAtTime(hz / 2, ctx.currentTime);
    gainPad.gain.setValueAtTime(0.06, ctx.currentTime);
    oscPad.connect(gainPad);
    gainPad.connect(masterGain);
    oscPad.start();
    oscillators.push(oscPad);

    return oscillators;
  }, [hz, binauralPreset]);

  const startAudio = useCallback(() => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const ctx = audioCtxRef.current;
    if (ctx.state === "suspended") ctx.resume();

    const masterGain = ctx.createGain();
    masterGain.gain.setValueAtTime(0, ctx.currentTime);
    masterGain.connect(ctx.destination);

    const oscillators = mode === "binaural"
      ? startBinauralBeat(ctx, masterGain)
      : startPureTone(ctx, masterGain);

    const vol = volume[0] / 100;
    masterGain.gain.linearRampToValueAtTime(vol * 0.4, ctx.currentTime + 1.2);

    nodesRef.current = { masterGain, oscillators };
    setIsPlaying(true);
    onPlayStateChange?.(true);
  }, [hz, volume, mode, startPureTone, startBinauralBeat, onPlayStateChange]);

  // Restart audio when mode or preset changes while playing
  useEffect(() => {
    if (isPlaying) {
      stopAudio();
      const timer = setTimeout(() => startAudio(), 500);
      return () => clearTimeout(timer);
    }
  }, [mode, binauralPreset]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (nodesRef.current?.masterGain && audioCtxRef.current) {
      const ctx = audioCtxRef.current;
      const vol = volume[0] / 100;
      nodesRef.current.masterGain.gain.cancelScheduledValues(ctx.currentTime);
      nodesRef.current.masterGain.gain.setValueAtTime(
        nodesRef.current.masterGain.gain.value, ctx.currentTime
      );
      nodesRef.current.masterGain.gain.linearRampToValueAtTime(vol * 0.4, ctx.currentTime + 0.15);
    }
  }, [volume]);

  useEffect(() => {
    if (autoPlay && !isPlaying) startAudio();
  }, [autoPlay]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => stopAudio();
  }, [stopAudio]);

  const toggle = () => {
    if (isPlaying) stopAudio();
    else startAudio();
  };

  const barCount = compact ? 5 : 9;
  const barHeights = Array.from({ length: barCount }, (_, i) => {
    const base = 0.3 + Math.sin((i / barCount) * Math.PI) * 0.7;
    return base;
  });

  // In binaural mode, alternate bar colors for L/R channel visual
  const getBarColor = (i) => {
    if (mode !== "binaural") return accentColor;
    return i % 2 === 0 ? accentColor : "#CDB4DB";
  };

  return (
    <div
      className={`rounded-2xl border border-black/5 ${compact ? "p-3" : "p-4"}`}
      style={{ backgroundColor: accentColor + "08" }}
      data-testid="frequency-player"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Main row: play, visualizer, info, volume */}
      <div className="flex items-center gap-3">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={toggle}
          data-testid="frequency-play-pause"
          className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300"
          style={{
            backgroundColor: isPlaying ? accentColor : accentColor + "15",
            color: isPlaying ? "#fff" : accentColor,
          }}
        >
          {isPlaying ? <Pause size={18} weight="fill" /> : <Play size={18} weight="fill" />}
        </motion.button>

        {/* Visualizer Bars */}
        <div className={`flex items-end gap-[3px] ${compact ? "h-6" : "h-8"}`}>
          {barHeights.map((h, i) => (
            <motion.div
              key={i}
              className="rounded-full"
              style={{
                width: compact ? 3 : 4,
                backgroundColor: getBarColor(i),
                opacity: isPlaying ? 0.85 : 0.25,
              }}
              animate={
                isPlaying
                  ? {
                      height: [
                        `${h * (compact ? 60 : 80)}%`,
                        `${(1 - h) * (compact ? 60 : 80) + 20}%`,
                        `${h * (compact ? 60 : 80)}%`,
                      ],
                    }
                  : { height: `${h * 40}%` }
              }
              transition={
                isPlaying
                  ? {
                      duration: mode === "binaural" ? 0.8 + i * 0.1 : 1.2 + i * 0.15,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }
                  : { duration: 0.4 }
              }
            />
          ))}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className={`font-medium text-[#1A2421] ${compact ? "text-xs" : "text-sm"}`}>
              {hz} Hz
            </p>
            {mode === "binaural" && (
              <span
                className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                style={{ backgroundColor: accentColor + "15", color: accentColor }}
              >
                {binauralPreset.beatHz} Hz beat
              </span>
            )}
          </div>
          {!compact && (
            <p className="text-[11px] text-[#78857F] truncate">
              {mode === "binaural"
                ? `${binauralPreset.name} — L: ${hz} Hz / R: ${hz + binauralPreset.beatHz} Hz`
                : frequencyText?.replace(/^\d+\s*Hz\s*[-–—]\s*/, "") || "Pure tone"}
            </p>
          )}
        </div>

        {/* Mode Toggle */}
        {!compact && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              const next = mode === "pure" ? "binaural" : "pure";
              setMode(next);
              if (next === "binaural") setShowPresets(true);
              else setShowPresets(false);
            }}
            data-testid="binaural-mode-toggle"
            className="shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-semibold transition-all duration-300 border"
            style={{
              backgroundColor: mode === "binaural" ? accentColor : "transparent",
              color: mode === "binaural" ? "#fff" : accentColor,
              borderColor: mode === "binaural" ? accentColor : accentColor + "30",
            }}
          >
            <Headphones size={13} weight={mode === "binaural" ? "fill" : "duotone"} />
            Binaural
          </motion.button>
        )}

        {/* Compact binaural toggle */}
        {compact && (
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => setMode(mode === "pure" ? "binaural" : "pure")}
            data-testid="binaural-mode-toggle-compact"
            className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all"
            style={{
              backgroundColor: mode === "binaural" ? accentColor : accentColor + "15",
              color: mode === "binaural" ? "#fff" : accentColor,
            }}
            title={mode === "binaural" ? "Binaural Beat on" : "Switch to Binaural"}
          >
            <Headphones size={13} weight={mode === "binaural" ? "fill" : "duotone"} />
          </motion.button>
        )}

        {/* Volume */}
        <div className="flex items-center gap-1.5 shrink-0">
          <SpeakerHigh size={compact ? 14 : 16} weight="duotone" style={{ color: accentColor }} />
          <Slider
            value={volume}
            onValueChange={setVolume}
            max={100}
            min={0}
            step={5}
            className={compact ? "w-12" : "w-16"}
            data-testid="frequency-volume"
          />
        </div>
      </div>

      {/* Binaural Preset Selector (non-compact only) */}
      <AnimatePresence>
        {mode === "binaural" && !compact && showPresets && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="mt-3 pt-3 border-t border-black/5">
              <div className="flex items-center gap-1.5 mb-2">
                <WaveTriangle size={14} weight="duotone" style={{ color: accentColor }} />
                <span className="text-[10px] font-semibold text-[#1A2421] uppercase tracking-wider">
                  Brainwave Preset
                </span>
                <span className="text-[9px] text-[#78857F] ml-1">
                  (use headphones)
                </span>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {BINAURAL_PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    data-testid={`binaural-preset-${preset.id}`}
                    onClick={() => setBinauralPreset(preset)}
                    className={`p-2 rounded-xl text-left transition-all duration-200 border ${
                      binauralPreset.id === preset.id
                        ? "shadow-sm"
                        : "border-transparent hover:border-current/10"
                    }`}
                    style={{
                      backgroundColor: binauralPreset.id === preset.id ? accentColor + "12" : "transparent",
                      borderColor: binauralPreset.id === preset.id ? accentColor + "40" : "transparent",
                    }}
                  >
                    <p className="text-[11px] font-semibold text-[#1A2421]">{preset.name}</p>
                    <p className="text-[9px] text-[#78857F] leading-tight">{preset.band}</p>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Compact binaural indicator */}
      {mode === "binaural" && compact && (
        <p className="text-[9px] text-center mt-1.5" style={{ color: accentColor }}>
          {binauralPreset.name} &middot; {binauralPreset.band}
        </p>
      )}
    </div>
  );
}
